import { createSlice } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { groupBy, isEmpty, isEqual, sortBy } from "lodash";
import { checkoutOrderRequest, paymentRequest } from "store/requests/payment.request";
import { noonOfDay } from "utility/date-fns";
import { PAID_PAYMENT_STATUS, PENDING_PAYMENT_STATUS } from "../../constants";
import {
  cancelOrderRequest,
  fetchFilteredOrdersRequest,
  fetchOrderPositionRequest,
  fetchOrdersRequest,
} from "../requests/order.requests";

const orderSliceInitialState = {
  filters: {},
  selectedOrder: null,
  orders: [],
  pagination: { total_pages: 1, active_page: 1 },
  status: "idle",
};

export const orderSlice = createSlice({
  name: "order",
  initialState: orderSliceInitialState,
  reducers: {
    setSelectedOrder: (state, action) => {
      state.selectedOrder = action.payload;
    },
    resetSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
    setOrderFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.status = "idle";
    },
    refetchOrders: (state) => {
      state.status = "idle";
    },
    resetOrderFiltersAndPagination: (state) => {
      if (isEmpty(state.filters) && state.pagination.active_page === 1) return;
      state.status = "idle";
      state.filters = {};
      state.pagination.active_page = 1;
    },
  },
  extraReducers: {
    [fetchOrdersRequest.pending]: (state, { meta }) => {
      if (!meta.arg) state.status = "loading";
      else {
        state.status = "refetching";
        state.pagination.active_page = meta.arg;
      }
    },
    [fetchOrdersRequest.fulfilled]: (state, { payload: { data } }) => {
      state.status = "succeeded";
      state.orders = data.orders;
      state.pagination.total_pages = data.pagination.total_pages === 0 ? 1 : data.pagination.total_pages;
    },
    [fetchOrdersRequest.rejected]: (state) => {
      state.status = "failed";
    },
    [fetchFilteredOrdersRequest.pending]: (state, { meta }) => {
      if (isEmpty(meta.arg) || !isEqual(meta.arg, state.filters)) state.pagination.active_page = 1;
      state.filters = meta.arg;
      state.status = "refetching";
    },
    [fetchFilteredOrdersRequest.fulfilled]: (state, { payload: { data } }) => {
      state.status = "succeeded";
      state.orders = data.orders;
      state.pagination.total_pages = data.pagination.total_pages === 0 ? 1 : data.pagination.total_pages;
    },
    [fetchFilteredOrdersRequest.rejected]: (state) => {
      state.status = "failed";
    },
    [fetchOrderPositionRequest.fulfilled]: (state, { payload: { data }, meta }) => {
      if (!data) return;

      const { location: geolocation = null, pickupEta = null, dropsEta = {} } = data;

      state.orders = state.orders.reduce((prev, curr) => {
        if (curr.id !== meta.arg) return [...prev, curr];
        return [
          ...prev,
          {
            ...curr,
            rider: { ...curr.rider, ...(geolocation && { geolocation }) },
            pick_up: { ...curr.pick_up, ...(pickupEta && { eta: pickupEta }) },
            drops: curr.drops.map((drop) => ({
              ...drop,
              ...(dropsEta?.[drop.reference] && { eta: dropsEta[drop.reference] }),
            })),
          },
        ];
      }, []);
    },
    [cancelOrderRequest.fulfilled]: (state, { payload: { data }, meta }) => {
      state.orders = state.orders.map((order) => (order.id === meta.arg ? data : order));
    },
    [checkoutOrderRequest.fulfilled]: (state, { payload: { data } }) => {
      const { client_secret, cards, paymentStatus, references, expire_at } = data;

      state.orders = state.orders.map((curr) => {
        if (references.includes(curr.reference)) {
          if ([PENDING_PAYMENT_STATUS, PAID_PAYMENT_STATUS].includes(paymentStatus)) return { ...curr, paymentStatus };
          return { ...curr, payment: { secret: client_secret, cards, expire_at } };
        }
        return curr;
      });
    },
    [paymentRequest.fulfilled]: (state) => {
      if (state.selectedOrder) {
        state.orders = state.orders.map((curr) => {
          if (curr.reference === state.selectedOrder) return { ...curr, paymentStatus: PAID_PAYMENT_STATUS };
          return curr;
        });
      }
    },
  },
});

export const { setSelectedOrder, resetSelectedOrder, setOrderFilters, refetchOrders, resetOrderFiltersAndPagination } =
  orderSlice.actions;

export const retrieveGroppedOrders = (state) => {
  return groupBy(
    sortBy(state.order.orders, ({ startDate }) => format(noonOfDay(new Date(startDate)), "yyyy-MM-dd HH:mm")).reverse(),
    ({ startDate }) => format(noonOfDay(new Date(startDate)), "yyyy-MM-dd HH:mm")
  );
};
export const retrieveOrders = (state) => state.order.orders;

export const retrieveSelectedOrder = (state) => {
  return state.order?.orders?.find((order) => order.reference === state.order?.selectedOrder) || {};
};

export const retrieveOrdersPagination = (state) => state.order.pagination;
export const retrieveOrderFilters = (state) => state.order.filters;

export const retrieveOrdersStatus = (state) => {
  const { status } = state.order;
  return { idle: status === "idle", loading: status === "loading", refetching: status === "refetching" };
};

export default orderSlice.reducer;
